<template>
  <view-wrapper
    name="Media library"
    :loading="loading"
    :loadingText="loadingText"
  >
    <modal
      v-if="showWarning"
      @close="showWarning = false"
      type="warning"
      heading="Warning"
      :description="warningText"
    />
    <modal
      v-if="confirmDeleteOpen"
      @close="confirmDeleteOpen = !confirmDeleteOpen"
      type="warning"
    >
      <template slot="body">
        <h3>Are you sure?</h3>
        <p>This action cannot be reverted</p>
      </template>
      <template slot="buttons">
        <grafokett-button
          class="mb-2 button big primary white"
          @click="confirmDeleteOpen = !confirmDeleteOpen"
          >Cancel
        </grafokett-button>
        <grafokett-button
          class="mb-2 button big primary white"
          @click="deleteFile()"
          >Delete
        </grafokett-button>
      </template>
    </modal>

    <expandable-table
      :columnsToNotSort="columnsToNotSort"
      :data="labels"
      :headers="headers"
      :useSearch="false"
    >
      <template v-slot:column="{ column, child }">
        <div v-if="column === 'folderName' && !child.isChild">
          <div class="flex items-center justify-between" @click.stop>
            <div class="flex-1" @click.stop>
              <template v-if="renamingFolder === child.folderName">
                <grafokett-input
                  class="w-72"
                  v-model="renameValue"
                  :editable="true"
                  labelPlaceholder="New name"
                  @click.stop
                  @keydown.enter.prevent="confirmRename(child.folderName)"
                  @keydown.esc.prevent="cancelRename"
                />
              </template>
              <span v-else>{{ child.folderName }}</span>
            </div>
            <div class="flex gap-2 items-center">
              <template v-if="renamingFolder === child.folderName">
                <grafokett-button
                  class="small primary white"
                  @click.stop="confirmRename(child.folderName)"
                >
                  Save
                </grafokett-button>
                <grafokett-button
                  class="small light"
                  @click.stop="cancelRename"
                >
                  Cancel
                </grafokett-button>
              </template>
              <template v-else>
                <a
                  class="text-sm underline text-highlight cursor-pointer"
                  @click.stop="startRename(child.folderName)"
                >
                  Rename
                </a>
              </template>
            </div>
          </div>
        </div>
        <span v-else>{{ child[column] }}</span>
      </template>

      <template v-slot:child="{ child }">
        <div v-for="(fileName, index) in child.files" :key="'var' + index">
          <div>
            <div class="flex items-center p-3 rounded bg-primary-light">
              <media-library-preview
                :folderName="child.folderName"
                :fileName="fileName"
              ></media-library-preview>
              <span class="flex-1">{{ fileName }}</span>
              <icon-button
                tooltip="Delete file"
                @click="openConfirmModal(child.folderName, fileName)"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  class="w-6 h-6 text-highlight"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="1"
                    d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636"
                  />
                </svg>
              </icon-button>
            </div>
          </div>
        </div>
      </template>
    </expandable-table>

    <wrapper class="p-5 pt-10 mb-3" :checkForChildFocused="true">
      <div>
        <div class="relative py-40" v-if="files.length < 1">
          <div class="absolute inset-0 flex flex-col p-4">
            <p class="mt-0">Upload files ({{ supportedFilesPattern }})</p>
            <Dropzone
              :multiple="true"
              @filesAdded="onFilesAdded"
              :accept="supportedFilesPattern"
            >
              Add files
            </Dropzone>
          </div>
        </div>
        <div class="p-4 rounded bg-primary-light" v-else>
          <div v-for="(file, index) in files" :key="'var' + index">
            {{ file.name }}
          </div>

          <grafokett-button
            class="float-right small"
            @click="
              () => {
                data = {};
                files = [];
              }
            "
            >Change files</grafokett-button
          >
        </div>

        <div class="relative py-10" v-if="files.length > 0">
          <grafokett-input
            class="w-96"
            v-model="targetFolder"
            :editable="true"
            labelPlaceholder="Target folder"
          />
        </div>

        <grafokett-button
          class="flex content-center w-40 mt-4 light"
          @click="uploadFiles"
          v-if="files.length > 0 && targetFolder"
        >
          <i class="p-1 upload-symbol" />
          <span class="flex-1 inline-block p-1 text-white">Upload</span>
        </grafokett-button>
      </div>
    </wrapper>

    <wrapper class="mb-4">
      <h4>ABOUT TEMPLATES</h4>
      You can upload some special templates that can be automatically matched.
      <br />
      <br />
      <b>Supported pattern examples:</b>
      <br />
      <pre>media-ref:articles/#EXTERNALIDENTIFIER#.png</pre>
      <br />Given an article with external identifier "artno12345", will look
      for a file matching "artno12345.png" in the folder "articles".
    </wrapper>
  </view-wrapper>
</template>

<script>
import GrafokettButton from "../components/forms/Button.vue";
import GrafokettInput from "../components/forms/Input.vue";
import IconButton from "../components/IconButton.vue";
import ExpandableTable from "../components/ExpandabelTable";
import Modal from "../components/Modal";
import Dropzone from "../components/Dropzone";
import MediaLibraryPreview from "../components/MediaLibraryPreview.vue";

export default {
  components: {
    Modal,
    GrafokettButton,
    GrafokettInput,
    IconButton,
    ExpandableTable,
    Dropzone,
    MediaLibraryPreview,
  },
  data() {
    return {
      headers: {
        folderName: "Folder name",
      },
      columnsToNotSort: ["arrow"],
      loading: false,
      loadingText: "List of folders and files",
      warningText: "",
      showWarning: false,
      fileToDelete: null,
      confirmDeleteOpen: false,
      folders: [],
      supportedFilesPattern:
        ".jpg,.png,.bmp,.jpeg,.jpe,.jfif,.git,.tif,.tiff,.wmf,.emf,.ico,.pcx,.psd,.pdf,.svg",
      files: [],
      targetFolder: undefined,
      renamingFolder: null,
      renameValue: "",
    };
  },
  computed: {
    labels() {
      var result = this.folders.map(d => ({
        ...d,
        expandableData: d,
        id: d.folderName,
        files: d.files,
      }));
      return result;
    },
  },
  methods: {
    async getData() {
      var data = await this.$store.dispatch("MEDIA_LIBRARY/GET_FILES");
      this.folders = data;
    },
    openConfirmModal(folderName, fileName) {
      this.confirmDeleteOpen = true;
      this.fileToDelete = {
        folderName: folderName,
        fileName: fileName,
      };
    },
    onFilesAdded(files) {
      this.files = files;
    },
    async uploadFiles() {
      while (this.files.length > 0) {
        var f = this.files.shift();
        try {
          this.loading = true;
          const formData = new FormData();
          formData.append("file", f);
          await this.$store.dispatch("MEDIA_LIBRARY/UPLOAD", {
            folderName: this.targetFolder,
            form: formData,
          });
        } catch (e) {
          console.error(e.message, e);
        }
      }

      this.loading = false;
      await this.getData();
    },
    async deleteFile() {
      this.confirmDeleteOpen = false;
      this.loading = true;

      try {
        await this.$store.$api.delete(
          `/api/mediaLibrary/folders/${this.fileToDelete.folderName}/files/${
            this.fileToDelete.fileName
          }`
        );
      } catch (e) {
        this.warningText = e.message;
        this.showWarning = true;
      }

      this.fileToDelete = null;
      this.getData();
      this.loading = false;
    },
    startRename(folderName) {
      this.renamingFolder = folderName;
      this.renameValue = folderName;
    },
    cancelRename() {
      this.renamingFolder = null;
      this.renameValue = "";
    },
    async confirmRename(folderName) {
      const newName = (this.renameValue || "").trim();
      if (!newName) {
        this.warningText = "Enter a name for the folder.";
        this.showWarning = true;
        return;
      }

      this.loading = true;
      try {
        await this.$store.dispatch("MEDIA_LIBRARY/RENAME_FOLDER", {
          folderName,
          newFolderName: newName,
        });
        this.renamingFolder = null;
        this.renameValue = "";
        await this.getData();
      } catch (e) {
        const message =
          (e.response && e.response.data && e.response.data.message) ||
          e.message ||
          "Could not rename the folder.";
        this.warningText = message;
        this.showWarning = true;
      } finally {
        this.loading = false;
      }
    },
  },
  async mounted() {
    this.loading = true;
    await this.getData();
    this.loading = false;
  },
};
</script>
